import { CAR_HAWKER_704_RULE, DUTY_CERT_704_LABEL } from "./carOps";
import { hawkerSopTypeLine, isHawker800xpType } from "./hawker800xp";
import { COMPANY_KIND_LABEL } from "./career";
import { formatDutyHours, type DutyCertificateKind, type DutySettings } from "./duty";
import { ftlSchemeLabel, inForcePreset, SIM_FTL_NOTE } from "./ftlScheme";
import type { AircraftSpec, CompanyKind, CompanyProfile } from "./types";

export type SopSectionId =
  | "ops_spec"
  | "company"
  | "fleet"
  | "duty"
  | "dispatch"
  | "weather_mel"
  | "postflight";

export type SopSection = {
  id: SopSectionId;
  title: string;
  body: string;
};

export type SopOverrides = Partial<Record<SopSectionId, string>>;

export type SopBinder = {
  certificateKind: DutyCertificateKind;
  certificateLabel: string;
  companyKind: CompanyKind;
  operatorName: string;
  generatedAtUtc: string;
  sections: SopSection[];
  overrides: SopOverrides;
};

export const SOP_SECTION_ORDER: SopSectionId[] = [
  "ops_spec",
  "company",
  "fleet",
  "duty",
  "dispatch",
  "weather_mel",
  "postflight",
];

export const SOP_SECTION_TITLE: Record<SopSectionId, string> = {
  ops_spec: "Ops spec",
  company: "Company",
  fleet: "Fleet",
  duty: "Duty / FDP",
  dispatch: "Dispatch / Inbox",
  weather_mel: "Weather / MEL",
  postflight: "Post-flight / AAR",
};

export function isSopSectionId(value: unknown): value is SopSectionId {
  return SOP_SECTION_ORDER.includes(value as SopSectionId);
}

export function hydrateSopOverrides(raw?: unknown): SopOverrides {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: SopOverrides = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (isSopSectionId(key) && typeof value === "string" && value.trim()) {
      out[key] = value;
    }
  }
  return out;
}

function fleetLines(fleet: AircraftSpec[], certLabel: string): string {
  if (!fleet.length) return "No airframes on the line. Add tails on Fleet (charter desk stays 2–10).";
  return fleet
    .map((a) => {
      const bits = [
        `${a.registration} ${a.name} (${a.icaoType})`,
        `cruise ${a.cruiseKt} kt`,
        `range ${a.rangeNm} NM`,
        `min rwy ${a.minRunwayFt} ft`,
        `max pax ${a.maxPax}`,
        `CAR ${certLabel}`,
        a.status !== "available" ? a.status : "",
      ].filter(Boolean);
      return `• ${bits.join(" · ")}`;
    })
    .join("\n");
}

export type GenerateSopInput = {
  company: CompanyProfile;
  fleet: AircraftSpec[];
  duty?: DutySettings;
  now?: Date;
  overrides?: SopOverrides;
};

export function generateSopSections(input: GenerateSopInput): SopSection[] {
  const company = input.company;
  const kind = company.companyKind;
  const cert = input.duty?.certificateKind ?? "704";
  const certLabel = input.duty?.certificateLabel ?? DUTY_CERT_704_LABEL;
  const limits = input.duty?.limits;
  const bases = [company.homeIcao, ...(company.extraBases ?? [])].filter(Boolean).join(", ");
  const types = (company.operatedIcaoTypes ?? []).join(", ") || "H25B / company types";

  const opsSpec = [
    `${company.operatorName} company certificate on this desk: CAR ${certLabel} (kind ${cert}).`,
    CAR_HAWKER_704_RULE,
    `Company kind: ${COMPANY_KIND_LABEL[kind]}. Generated SOP copy follows the Settings → Company CAR picker.`,
    "This binder is a company SOP aid generated from the desk (fleet, duty clock, Destinations). It is not a legal manual and is not Transport Canada approved. Picking 704 does not mean the duty clock implements a full CAR 704 scheme.",
    "Charter fleet stays 2–10 aircraft. Northstar is the software vendor; the operator name on the desk is the company.",
  ].join("\n\n");

  const companyBody = [
    `Operator: ${company.operatorName}`,
    `Home: ${company.homeIcao}${bases && bases !== company.homeIcao ? ` · Bases on file: ${bases}` : ""}`,
    `Operated types: ${types}`,
    company.useTelephonyCallsign
      ? `Telephony: ${company.telephonyName || "on"} (opt-in).`
      : "Radio / ATC id is the tail registration unless telephony is turned on in Settings.",
    "Dual sign-on: Dispatcher owns the board, Inbox, catalogs, and binder edits. Pilot sees assigned trips, log, weather, map, and this binder read-only unless they are also a dispatcher.",
  ].join("\n");

  const fleetBody = [
    "Assign only a tail the company pilot is rated on unless Dispatcher overrides.",
    "Grounded MEL, overdue inspection, or a ground-squawk blocks Inbox auto-assign.",
    "Hawker mx follows a Transport Canada AMS (manufacturer Chapter 5): E/F/G calendar, 400 h hourly groups, 144-month gear overhaul. No mandatory 50-hour inspection on the 800XP.",
    input.fleet.some((a) => isHawker800xpType(a.icaoType)) ? hawkerSopTypeLine() : "",
    fleetLines(input.fleet, certLabel),
  ].filter(Boolean).join("\n\n");

  const inForce = inForcePreset(company, input.duty);
  const dutyBody = [
    `Company ops category on file: CAR ${certLabel} (kind ${cert}). A CAR preset changes this certificate.`,
    `In-force duty clock: ${ftlSchemeLabel(company.ftlScheme ?? "car704")} — ${inForce.label}. Can: ${inForce.can} Can’t: ${inForce.cant}`,
    SIM_FTL_NOTE,
    limits
      ? `FDP ${formatDutyHours(limits.maxFdpMin)} · flight time ${formatDutyHours(limits.maxFlightTimeMin)} · min rest ${formatDutyHours(limits.minRestMin)} · warn at ${Math.round(limits.warnRatio * 100)}%. These are training-aid presets, not Transport Canada tables.`
      : "Duty limits live on Settings → Company (FTL scheme + duty table preset).",
    "What this clock counts today: manual Start duty / End duty / Start rest; flight time credited from completed flight logs while on duty; soft warn at 80% and red at the limit.",
    "What this clock does not count: report time, deadhead as duty, standby-as-duty, weekly / 7-day lookbacks, or a full CAR 704 / FAR 135 / EASA table.",
    "Duty Standby (Wave D) is an on-demand call when armed — not a background ops brain and not duty credit.",
  ].join("\n\n");

  const dispatchBody = [
    "Job modes: On-demand (Fetch / Templates) or position-aware Automatic (Propose nearby).",
    "Automatic uses fused position (MSFS SimConnect on this PC is primary, SI when the sim sample is down, last-known if the sim is closed). Accept / File stays confirm-gated.",
    "Inbox generation is on-demand Fetch (Grok when a key is set, else seasonal fallback) plus optional auto-inbox while the desk is open (on-open seed if Inbox is empty, then the Settings daily-rate interval). Settings → Desk defaults to ~2–3 RFQs per sim day (small-charter); a faster busy-desk rate is optional. Cap still applies. Auto-inbox prefers cheap Grok per tick when a key is set (structured JSON + one retry; max two calls). Classic only after no key, xAI outage / rate-limit, or last-resort empty / filter-emptied set — Status lane says which one ran. Away: pause company (recommended) — closing the desk freezes the Grok schedule brain (no schedule catch-up, no invented ferry). Idle Inbox refill (Settings → Desk, on by default with tight caps) may add a few Inbox RFQs while the desk is open and idle, or on reopen — a two-week vacation does not mint a two-week pile. Grok suggestions are rate-card clamped to desk catalogs — Grok does not invent money or move the ledger. Settings → Desk Dispatcher brain: Grok is the dispatcher (default). Classic is the safety net — explicit OFF, or automatic when the API fails or a plan is unsafe. Grok uses the same xAI key as Inbox Fetch with a company-dispatcher brief (range, runway, duty, ferry, overlaps, tech-stops, Pilot-first) plus the Dispatcher-written company charter brief (Dispatch board → Charter brief). Toggle off leaves the board as-is (optional restore of the pre-Grok snapshot). Local trickle and Grok Fetch both apply CYPD realism: no jet on short Atlantic hops (CYPD–CYHZ / CYPD–CYQM), season-matched purposes (no Cape Breton golf in winter), executive / GA / reliever destinations first (CYPD→KTEB house mix) with airline hubs as backup when no exec field is usable and as primary for missed-connection stories — the company charter brief may tighten or relax that dest mix — and a paying charter on an mx positioning leg when the sector is legal. Empty company / mx / return ferries are never revenue; a taken cheaper-than-usual mx-positioning charter or discounted backhaul fill is.",
    "Career never locks the Casual generator (nav Generate).",
    "File a trip from Inbox → board. If the tail is not already at the charter origin, Classic local math files a linked empty ferry (never revenue) so Flight plans can be opened for the positioning leg, then the charter. If the next revenue already starts at the away field, overnight there instead of an empty home out-and-back (Megan rings). Closing the sim after a parked arrival keeps the last-known field — no teleport home. Ferry STD/STA respect per-airport quiet hours / curfews (see docs/FERRY_CURFEW.md); when no legal window exists the desk warns instead of silently filing illegal times. Flight plans stay on the existing panel.",
  ].join("\n\n");

  const wxBody = [
    "Weather brief uses NOAA / AviationWeather first. Flight tracker getWX if a key is already on the desk. VATSIM METAR last. Trip and Flight plan views show flight-affecting NOTAMs for dep/dest only. Never invent METAR, TFR, or NOTAM text.",
    "MEL / deferred defects live on Maintenance. Ground severity grounds the tail.",
    "Fuel, landing, handling, and extras come from Catalogs. Rate tables are helpers only.",
  ].join("\n\n");

  const aarBody = [
    "After a trip is complete, open Flight Log → AAR.",
    "File an arrival report: what happened, maintenance / squawk, and fuel used in that tail’s Settings unit (lb, kg, US gal, or L). Nothing-to-report still accepts a fuel figure.",
    "Fuel used posts against Catalogs / rate-card US-gal prices (Jet-A 6.7 lb/US gal when converting mass). It replaces any estimated trip fuel line.",
    "Track is best-effort from fused map / log samples. If the track is sparse, add ICAO or lat/lon waypoints by hand.",
    "P&L reads posted ledger lines for the trip, or a catalog / rate-card estimate when nothing has posted. The desk owns the numbers.",
    "Manual post-flight (air / engine / APU / fuel) remains truth for Mx times.",
  ].join("\n\n");

  return [
    { id: "ops_spec", title: SOP_SECTION_TITLE.ops_spec, body: opsSpec },
    { id: "company", title: SOP_SECTION_TITLE.company, body: companyBody },
    { id: "fleet", title: SOP_SECTION_TITLE.fleet, body: fleetBody },
    { id: "duty", title: SOP_SECTION_TITLE.duty, body: dutyBody },
    { id: "dispatch", title: SOP_SECTION_TITLE.dispatch, body: dispatchBody },
    { id: "weather_mel", title: SOP_SECTION_TITLE.weather_mel, body: wxBody },
    { id: "postflight", title: SOP_SECTION_TITLE.postflight, body: aarBody },
  ];
}

export function buildSopBinder(input: GenerateSopInput): SopBinder {
  const now = input.now ?? new Date();
  const overrides = hydrateSopOverrides(input.overrides);
  return {
    certificateKind: input.duty?.certificateKind ?? "704",
    certificateLabel: input.duty?.certificateLabel ?? DUTY_CERT_704_LABEL,
    companyKind: input.company.companyKind,
    operatorName: input.company.operatorName,
    generatedAtUtc: now.toISOString(),
    sections: generateSopSections(input),
    overrides,
  };
}

export function sectionBody(binder: SopBinder, id: SopSectionId): { body: string; overridden: boolean } {
  const generated = binder.sections.find((s) => s.id === id)?.body ?? "";
  const override = binder.overrides[id]?.trim();
  if (override) return { body: override, overridden: true };
  return { body: generated, overridden: false };
}

export function setSopOverride(overrides: SopOverrides, id: SopSectionId, body: string): SopOverrides {
  const next = { ...overrides };
  if (!body.trim()) {
    delete next[id];
    return next;
  }
  next[id] = body;
  return next;
}

export function clearSopOverride(overrides: SopOverrides, id: SopSectionId): SopOverrides {
  const next = { ...overrides };
  delete next[id];
  return next;
}
